import { forwardRef, useEffect, useState, type InputHTMLAttributes } from 'react';
import { FormInputContainer, Input, ErrorMessage } from '../Input/Input.style';
import { ErrorSolidIcon } from '@icons';
import { FormLabel } from '..';

const brlFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

export function formatBrl(valor: number): string {
  const formatado = brlFormatter.format(valor);
  return formatado;
}

/** Dígitos digitados interpretados como centavos (ex.: `1234` → R$ 12,34). */
export function digitsToAmount(digits: string): number {
  if (!digits) return 0;
  return parseInt(digits, 10) / 100;
}

export interface FormInputCurrencyProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  errorText?: string;
  label?: string;
  tooltip?: {
    title: string;
    content: string;
  };
  value?: number;
  onChange?: (amount: number) => void;
}

const FormInputCurrency = forwardRef<HTMLInputElement, FormInputCurrencyProps>(
  (
    {
      errorText,
      label,
      tooltip,
      id,
      value,
      onChange,
      onBlur,
      name,
      placeholder = 'R$ 0,00',
      ...rest
    },
    ref
  ) => {
    const [display, setDisplay] = useState(() =>
      value !== undefined && value !== null && Number.isFinite(value) ? formatBrl(Number(value)) : ''
    );

    useEffect(() => {
      setDisplay((prev) => {
        const prevDigits = prev.replace(/\D/g, '');
        const prevAmount = digitsToAmount(prevDigits);

        if (value === undefined || value === null || !Number.isFinite(Number(value))) {
          return prevDigits === '' ? prev : '';
        }

        const next = Number(value);
        if (Math.round(prevAmount * 100) === Math.round(next * 100)) return prev;
        return formatBrl(next);
      });
    }, [value]);

    return (
      <FormInputContainer>
        {label && (
          <FormLabel htmlFor={id} tooltip={tooltip}>
            {label}
          </FormLabel>
        )}
        <Input
          {...rest}
          ref={ref}
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={display}
          placeholder={placeholder}
          hasError={!!errorText}
          lang="pt-BR"
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            if (!digits) {
              setDisplay('');
              onChange?.(0);
              return;
            }
            const amount = digitsToAmount(digits);
            const formatado = formatBrl(amount);
            setDisplay(formatado);
            onChange?.(amount);
          }}
          onBlur={onBlur}
        />
        {errorText && (
          <ErrorMessage>
            <ErrorSolidIcon /> <span>{errorText}</span>
          </ErrorMessage>
        )}
      </FormInputContainer>
    );
  }
);

FormInputCurrency.displayName = 'FormInputCurrency';

export default FormInputCurrency;
